import {generateGuid} from '@packages/utilities/guid';
import {produce} from 'immer';

import {
  ActionPresetGroupId,
  ActionPresetId,
  BaseTriggerConfig,
  ExtensionKey,
  TargetDefinition,
  TimelineControlType,
  TimelineAction,
  TimelineActionId,
  TimelineId,
  TimelinePosition,
  Trigger,
  ConditionalPlayback,
  TweenType,
  Timeline,
  Interaction,
} from '@packages/systems/ix3/core';
import {generateTimelineId} from '@packages/systems/ix3/shared';
import {IX3_WF_EXTENSION_KEYS} from '@packages/systems/ix3-wf-plugins/shared';
import {emptyArray, entries} from '@packages/utilities/fp/utils';
import {IX3_TIMELINE_PLAYBAR_HEIGHT, IX3_ACTION_DURATION} from './constants';
import {IX3State} from './types';
import {BUILT_IN_PRESETS} from './defaultPresets';
import type {TimelineRoleDefinition} from '@packages/systems/ix3/core';

const GUID_LENGTH = 8;
// @todo Have this imported from IX3 core somehow.

export const IX3_ID_PREFIXES = {
  interaction: 'i-',
  timeline: 't-',
  actionPreset: 'a-',
  actionPresetGroup: 'ag-',
  timelineAction: 'ta-',
} as const;

export {
  generateInteractionId,
  generateTimelineId,
} from '@packages/systems/ix3/shared';

import {
  SCROLL_DIRECTION_TOGGLES,
  SCROLL_TOGGLE_DEFAULTS,
  targetTransforms,
} from '@packages/systems/ix3/schema';
export const hasFiltering = targetTransforms.hasFilter;
export const getFilterContext = targetTransforms.getFilter;

// ============================================================================
// IDs
// ============================================================================

/**
 * Generates a unique id for a user-managed timeline group. Stored on
 * `timeline.groupId` and referenced from `triggerConfig.assignedGroupId`.
 */
export function generateGroupId(generate = generateGuid): string {
  return generate(GUID_LENGTH);
}

// ============================================================================
// Multi-Timeline Utilities
// ============================================================================

/**
 * Result of creating multiple timelines for a multi-timeline trigger.
 */
export interface MultiTimelineSet {
  /** Array of generated timeline IDs in the same order as the input roles */
  timelineIds: TimelineId[];
  /** Array of trigger metadata (role) corresponding to each timeline */
  metadata: Array<{
    role: string;
  }>;
}

/**
 * Creates multiple timelines for a multi-timeline trigger with proper metadata.
 * This utility ensures consistent timeline ID generation and role assignment.
 *
 * Note: triggerExtensionKey is NOT stored - it can always be derived from interaction.triggers[0][0]
 *
 * @param _triggerExtensionKey - Extension key (unused, kept for backwards compat with callers)
 * @param roles - Array of role definitions for each timeline
 * @param generate - Optional custom GUID generator (for testing)
 * @returns MultiTimelineSet with timeline IDs and role metadata
 *
 * @example
 * ```typescript
 * const result = createMultiTimelineSet('wf:mouse-move', [
 *   { role: 'mouseX', label: 'Mouse X' },
 *   { role: 'mouseY', label: 'Mouse Y' },
 * ]);
 * // result.timelineIds = ['t-abc12345', 't-def67890']
 * // result.metadata contains role for each timeline
 * ```
 */
export function createMultiTimelineSet(
  _triggerExtensionKey: ExtensionKey,
  roles: TimelineRoleDefinition[],
  generate = generateGuid
): MultiTimelineSet {
  const timelineIds: TimelineId[] = [];
  const metadata: MultiTimelineSet['metadata'] = [];

  for (const roleDef of roles) {
    const timelineId = generateTimelineId(generate);
    timelineIds.push(timelineId);
    metadata.push({
      role: roleDef.role,
    });
  }

  return {
    timelineIds,
    metadata,
  };
}

export function generateActionPresetId(
  generate = generateGuid
): ActionPresetId {
  return `${IX3_ID_PREFIXES.actionPreset}${generate(GUID_LENGTH)}`;
}

export function generateActionPresetGroupId(
  generate = generateGuid
): ActionPresetGroupId {
  return `${IX3_ID_PREFIXES.actionPresetGroup}${generate(GUID_LENGTH)}`;
}

// ============================================================================
// Timeline Label Utilities
// ============================================================================

/**
 * Type for looking up trigger data from registry.
 * Used to derive timeline labels and per-role playback defaults. The role
 * shape is re-exported from `TimelineRoleDefinition` so the lookup stays in
 * sync with the builder-level definition.
 */
export type TriggerDataLookup = (
  extensionKey: ExtensionKey
) =>
  | {schema?: {multiTimelineRoles?: readonly TimelineRoleDefinition[]}}
  | undefined;

/**
 * The role definition for `role`, from whichever of the interaction's triggers
 * declares it. Trigger order is authored data — a schema-valid interaction can
 * list a Click or Page load first and the role-declaring Hover second — so
 * reading `triggers[0]` alone loses the role's label and per-role playback
 * defaults for those interactions.
 */
function findRoleDefinition(
  role: string,
  interaction: Interaction | null | undefined,
  getTriggerData: TriggerDataLookup
): TimelineRoleDefinition | undefined {
  for (const trigger of interaction?.triggers ?? []) {
    const extensionKey = trigger?.[0];
    if (!extensionKey) continue;
    const definition = getTriggerData(
      extensionKey as ExtensionKey
    )?.schema?.multiTimelineRoles?.find((def) => def.role === role);
    if (definition) return definition;
  }
  return undefined;
}

/**
 * Looks up the role definition for a timeline using the trigger registry.
 * Returns `undefined` when the timeline has no role or no trigger schema
 * declares it.
 */
export function findTimelineRoleDef(
  timeline: Timeline | undefined,
  interaction: Interaction | null | undefined,
  getTriggerData: TriggerDataLookup
): TimelineRoleDefinition | undefined {
  const role = timeline?.triggerMetadata?.role;
  if (!role) return undefined;
  return findRoleDefinition(role, interaction, getTriggerData);
}

/**
 * Gets the display label for a timeline's action section.
 * Used in accordion headers and timeline playbar.
 *
 * @example
 * getTimelineActionLabel(mouseXTimeline, interaction, getTriggerData)
 * // Returns: "Mouse X"
 */
export function getTimelineActionLabel(
  timeline: Timeline | undefined,
  interaction: Interaction | null,
  getTriggerData: TriggerDataLookup
): string {
  // Prefer explicit name (user-created timeline groups)
  if (timeline?.name) return timeline.name;

  // Fall back to role-derived label (mousemove, navbar, dropdown, hover)
  const role = timeline?.triggerMetadata?.role;
  if (!role) return 'Actions';

  return (
    findRoleDefinition(role, interaction, getTriggerData)?.label ?? 'Actions'
  );
}

// ============================================================================
// Timeline Reuse Validation
// ============================================================================

/**
 * Detects if reusing a source timeline would create a circular reference.
 * Follows the reuse chain to check if targetTimelineId appears anywhere.
 *
 * @param sourceTimelineId - Timeline we want to reuse
 * @param targetTimelineId - Timeline we're configuring reuse for
 * @param allTimelines - All timelines in the store
 * @returns true if circular reference would be created
 *
 * @example
 * // A reuses B, B reuses C, user tries to make C reuse A
 * wouldCreateCircularReference('A', 'C', timelines) → true
 */
export function wouldCreateCircularReference(
  sourceTimelineId: string,
  targetTimelineId: string,
  allTimelines: Record<string, Timeline>
): boolean {
  const visited = new Set<string>();
  let currentId: string | undefined = sourceTimelineId;

  const MAX_DEPTH = 10; // Matches AnimationCoordinator.MAX_ALIAS_DEPTH
  let depth = 0;

  while (currentId && depth < MAX_DEPTH) {
    if (currentId === targetTimelineId) return true;
    if (visited.has(currentId)) return false;

    visited.add(currentId);
    const timeline: Timeline | undefined = allTimelines[currentId];
    currentId = timeline?.reuse?.sourceTimelineId;
    depth++;
  }

  return false;
}

/**
 * Checks if a timeline has any actions.
 * Returns false if timeline doesn't exist or has empty actions array.
 */
export function timelineHasActions(
  timelineId: string,
  allTimelines: Record<string, Timeline>
): boolean {
  const timeline = allTimelines[timelineId];
  return Boolean(timeline?.actions?.length);
}

export function generateTimelineActionId(
  generate = generateGuid
): TimelineActionId {
  return `${IX3_ID_PREFIXES.timelineAction}${generate(GUID_LENGTH)}`;
}

// ============================================================================
// Trigger
// ============================================================================
/**
 * Creates a new trigger with the given type.
 */
export function createTrigger<TConfig extends BaseTriggerConfig>(
  /** Extension key for the trigger */
  extensionKey: ExtensionKey,
  /** Configuration for the trigger */
  config: TConfig,
  /** Target definition for the trigger */
  targetDefinition?: TargetDefinition
): Trigger {
  return targetDefinition
    ? [extensionKey, config, targetDefinition]
    : [extensionKey, config];
}

/**
 * The trigger list an interaction will have after a delete, or null when the
 * reducer will decline it.
 *
 * Exists so the reducer and the Designer agree by construction. The
 * component-interaction reconcile has to reason about the POST-edit trigger list
 * — to count component triggers and to see rows a patch would otherwise add
 * twice — and it used to rebuild that list by hand alongside its dispatch. Any
 * guard added to the reducer would silently desync the two.
 *
 * Null rather than the unchanged list, so a caller cannot mistake a refused
 * delete for one that happened.
 */
export function triggersAfterDelete<T>(
  triggers: readonly T[],
  index: number
): T[] | null {
  // Never strip the last trigger — an interaction with no trigger can never
  // fire. Mirrored in the IX3_TRIGGER_DELETED reducer.
  if (triggers.length <= 1) return null;
  if (index < 0 || index >= triggers.length) return null;
  return triggers.filter((_, at) => at !== index);
}

/**
 * The trigger list an interaction will have after an update, or null when the
 * reducer will decline it. See {@link triggersAfterDelete}.
 */
export function triggersAfterUpdate<T>(
  triggers: readonly T[],
  index: number,
  trigger: T
): T[] | null {
  if (index < 0 || index >= triggers.length) return null;
  return triggers.map((existing, at) => (at === index ? trigger : existing));
}

export function createBaseTriggerConfig(
  controlType?: TimelineControlType,
  extensionKey?: ExtensionKey
): BaseTriggerConfig {
  if (controlType == null) return {};

  switch (controlType) {
    case TimelineControlType.LOAD: {
      return {controlType};
    }

    case TimelineControlType.SCROLL: {
      // "Scroll scrub", "Scroll actions", and "Scroll direction" share
      // controlType SCROLL, so the extension key is the only signal that
      // distinguishes them at creation time. The action variants fire toggle
      // actions at thresholds and must NOT scrub (scrub: null); the scrubbing
      // variants (wf:scroll, wf:scroll-scrub) keep the 0.8 smoothing default.
      if (extensionKey === IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION) {
        // Scroll direction implies its whole toggle-action set — play on the
        // way down, reverse on the way up — so none of it is user-chosen.
        // `end` starts when the trigger's bottom reaches the viewport's bottom
        // — the end of the page, for the page target. `clamp` stays off for the
        // page target, where Start is an offset into the page rather than an
        // element entering the viewport.
        return {
          controlType,
          scrollTriggerConfig: {
            showMarkers: false,
            clamp: false,
            scrub: null,
            start: 'top top',
            end: 'bottom bottom',
            ...SCROLL_DIRECTION_TOGGLES,
            // 'down' is already the runtime's default polarity, so seeding it
            // changes no playback. It's written so the config carries its own
            // "this came from Scroll direction" signal — the runtime builds
            // GSAP vars from `scrollTriggerConfig` alone and can't see the
            // extension key, and no other field combination is unique to this
            // trigger.
            scrollDirection: 'down' as const,
            split: true as const,
          },
        };
      }
      const isScrollActions =
        extensionKey === IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS;
      return {
        controlType,
        scrollTriggerConfig: {
          showMarkers: false,
          clamp: true,
          scrub: isScrollActions ? null : 0.8,
          start: 'top bottom',
          end: 'bottom top',
          ...SCROLL_TOGGLE_DEFAULTS,
          // Legacy `wf:scroll` keeps the pre-split runtime; see `split` in the
          // scroll trigger schema.
          ...((extensionKey === IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB ||
            isScrollActions) && {split: true as const}),
        },
      };
    }

    case TimelineControlType.STANDARD: {
      return {controlType};
    }

    case TimelineControlType.CONTINUOUS: {
      return {controlType};
    }

    default: {
      const _exhaustiveCheck: never = controlType;
      return {};
    }
  }
}

// ============================================================================
// Target
// ============================================================================

/**
 * Extract extension key from a target definition
 */
export function getTargetExtensionKey(
  target: TargetDefinition<unknown>
): ExtensionKey {
  return target[0];
}

/**
 * Creates an extension key from a namespace and ID.
 */
export function createExtensionKey(
  namespace: string,
  id: string
): ExtensionKey {
  return `${namespace}:${id}`;
}

/**
 * Parses an extension key into its namespace and ID.
 */
export function parseExtensionKey(
  extensionKey: ExtensionKey
): [string, string] {
  const parts = extensionKey.split(':');
  const namespace = parts[0];
  const categoryId = parts[1];
  if (!namespace || !categoryId) {
    throw new Error(`Invalid extension key: ${extensionKey}`);
  }
  return [namespace, categoryId];
}

// ============================================================================
// Timeline Action
// ============================================================================
/**
 * Available colors for timeline actions in the UI
 */
export const timelineActionColors = [
  'pink',
  'blue',
  'orange',
  'purple',
] as const;

export type TimelineActionColor = (typeof timelineActionColors)[number];
/**
 * Cycles between pink, blue, orange, purple, (repeat).
 */
export const getColorOfNextTimelineAction = (
  timelineActions: TimelineAction[]
) => {
  const count = timelineActions.length;
  const colorIndex = count % timelineActionColors.length;

  return timelineActionColors[colorIndex] || timelineActionColors[0];
};

/**
 * Calculates the next position for a timeline action.
 * It does this by looking at the furthest end time of all the timeline actions.
 */
export const getNextPositionForTimelineAction = (
  timelineActions: TimelineAction[]
) => {
  const furthestEndTime = timelineActions.reduce((furthest, current) => {
    /**
     * For now, to calculate the next position, we need to use numeric
     * values for the position and duration.
     */
    let currentPosition = Number(current.timing.position);
    if (isNaN(currentPosition)) {
      currentPosition = 0;
    }
    const currentDuration = current.timing.duration
      ? Number(current.timing.duration)
      : current.tt === TweenType.Set
        ? 0
        : 0.5;
    const currentEndTime = currentPosition + currentDuration;

    return Math.max(furthest, currentEndTime);
  }, 0);

  return furthestEndTime;
};

/**
 * Converts a duration to seconds.
 * Note: We'll probably need to consolidate this logic with similar
 * util logic in the Timeline component area.
 */
export const getSecondsFromPosition = (position?: TimelinePosition): number => {
  if (!position) {
    return 0;
  }

  const isMs = typeof position === 'string' && position.endsWith('ms');

  if (isMs) {
    return parseFloat(position) / 1000;
  }

  if (typeof position === 'number') {
    return position;
  }

  return 0;
};

export function convertTimeUnit(value: number, unit: string) {
  return unit === 's' ? value / 1000 : value * 1000;
}

export function getTimeUnitValue(value: number | string | undefined) {
  return typeof value === 'string'
    ? {value: parseFloat(value), unit: 'ms'}
    : {value: value ?? null, unit: 's'};
}

export const sortActionsByStartTime = (
  actions: TimelineAction[]
): TimelineAction[] => {
  return actions.sort((a, b) => {
    const aStartTime = getSecondsFromPosition(a.timing.position);
    const bStartTime = getSecondsFromPosition(b.timing.position);

    return aStartTime - bStartTime;
  });
};

export const sortActionsByIndex = (
  actions: TimelineAction[],
  actionIds: TimelineActionId[],
  newIndex: number
): TimelineAction[] => {
  // Create a set for faster lookup of selected action IDs
  const selectedIds = new Set(actionIds);

  // Create a new array without the selected actions
  const remainingActions = actions.filter(
    (timelineAction) => !selectedIds.has(timelineAction.id)
  );

  // Create the selected actions array in their original relative order
  const selectedActions = actions.filter((timelineAction) =>
    selectedIds.has(timelineAction.id)
  );

  // Calculate how many selected actions come before the target index
  const selectedActionsBeforeTarget = Array.from(selectedIds).filter((id) => {
    const timelineAction = actions.find((a) => a.id === id);
    return timelineAction && actions.indexOf(timelineAction) < newIndex;
  }).length;

  // The adjusted target index is the target minus the number of selected actions that came before it
  // This gives us the position in the remaining array where we should insert
  // However, the target index represents inserting after the action at that position
  // So we need to add 1 to get the correct insertion point
  const adjustedTargetIndex = newIndex + 1 - selectedActionsBeforeTarget;

  // Clamp the adjusted target index to valid bounds
  const clampedTargetIndex = Math.max(
    0,
    Math.min(remainingActions.length, adjustedTargetIndex)
  );

  // Insert the selected actions at the adjusted target position
  return [
    ...remainingActions.slice(0, clampedTargetIndex),
    ...selectedActions,
    ...remainingActions.slice(clampedTargetIndex),
  ];
};

/**
 * Used in CanvasResizeStore to calculate the height of the Timeline in order
 * to correctly calculate the height of the canvas's workspace to render
 * the iFrame's contents to scale.
 */
export function getIX3TimelineHeightFromState(state: IX3State) {
  const {selectedInteractionId, uiShowTimeline, uiTimelineHeight} = state;

  // If we don'thave a selected interaction...
  // We don't need to show the Timeline at all.
  if (!selectedInteractionId) {
    return 0;
  }

  // Accounting for either the entire Timeline or just the Timeline's Playbar.
  const timelineHeight = uiShowTimeline
    ? uiTimelineHeight
    : IX3_TIMELINE_PLAYBAR_HEIGHT;

  return timelineHeight;
}

// Utilities
export function arrayOrEmpty<T>(array?: T[] | null): T[] {
  return array ?? emptyArray;
}

/**
 * Sorts conditional playback array to ensure prefers-reduced-motion conditions
 * are always evaluated first, followed by breakpoint conditions.
 * This reflects the priority order in the UI and runtime evaluation.
 */
export function sortConditionalPlayback(
  conditionalPlayback: ConditionalPlayback | null | undefined
): ConditionalPlayback | null | undefined {
  if (!conditionalPlayback || conditionalPlayback.length <= 1) {
    return conditionalPlayback;
  }

  // Sort with prefers-reduced-motion first, then breakpoint conditions
  const sorted = conditionalPlayback.slice().sort((a, b) => {
    if (
      a.type === 'prefers-reduced-motion' &&
      b.type !== 'prefers-reduced-motion'
    ) {
      return -1; // a comes first
    }
    if (
      a.type !== 'prefers-reduced-motion' &&
      b.type === 'prefers-reduced-motion'
    ) {
      return 1; // b comes first
    }
    return 0; // maintain relative order for same types
  });

  return sorted;
}

// ============================================================================
// Action Conversion Utilities
// ============================================================================

/**
 * Converts a standard timeline action to a Set action.
 * - Changes tween type to Set (3)
 * - Clears timing properties that don't apply to instant tweens
 * - Transforms property values to only keep "to" values
 */
export function convertActionToSet(action: TimelineAction): TimelineAction {
  return produce(action, (draft) => {
    // Change tween type to Set
    draft.tt = TweenType.Set;

    // Clear timing properties that don't apply to Set actions
    draft.timing.duration = 0;
    draft.timing.ease = undefined;
    draft.timing.repeat = undefined;
    draft.timing.repeatDelay = undefined;
    draft.timing.yoyo = undefined;
    draft.timing.stagger = undefined;

    // Clear split text (not applicable to Set)
    draft.splitText = undefined;

    // Transform properties to only keep "to" values
    for (const [, props] of entries(draft.properties)) {
      for (const propKey in props) {
        const value = props[propKey];
        if (Array.isArray(value)) {
          if (value.length === 2) {
            // [from, to] -> [null, to] (prefer to value, fallback to from)
            props[propKey] = [null, value[1] ?? value[0]];
          } else if (value.length === 1) {
            // [from] -> [null, from] (use from as the set value)
            props[propKey] = [null, value[0]];
          }
        }
        // Single values stay as-is (they're already "to" values)
      }
    }
  });
}

/**
 * Converts a Set action to a standard "To" action.
 * - Changes tween type to To (0)
 * - Sets default duration
 * - Removes non-animatable properties
 */
export function convertActionToCustom(
  action: TimelineAction,
  registry?: {
    getPropertySchema: (
      propertyId: string,
      extensionKey: ExtensionKey
    ) => {animatable?: boolean} | undefined;
  }
): TimelineAction {
  return produce(action, (draft) => {
    draft.tt = TweenType.To;
    draft.timing.duration = IX3_ACTION_DURATION;

    // Remove non-animatable properties if switching from Set > Animate
    if (registry) {
      for (const [extensionKey, props] of entries(draft.properties)) {
        if (!props) continue;

        const filteredProps: Record<string, any> = {};
        for (const propKey in props) {
          const propertySchema = registry.getPropertySchema(
            propKey,
            extensionKey
          );
          if (propertySchema?.animatable !== false) {
            filteredProps[propKey] = props[propKey];
          }
        }
        draft.properties[extensionKey] = filteredProps;
      }
    }
  });
}

/**
 * Determines if an action can be converted (excludes plugin actions with custom tweens).
 * Built-in presets and custom animations are convertible.
 */
export function canConvertAction(action: TimelineAction): boolean {
  // Actions without presetId are custom animations - these are convertible
  if (!action.presetId) return true;

  // Built-in presets (`preset-*` / BUILT_IN_PRESETS) and user-saved presets
  // (`a-*`, see generateActionPresetId) are convertible — they're Set/Animate
  // actions, not plugin actions with custom editors (those are gated separately
  // via hasCustomEditor). Actions created from a saved preset now carry its
  // `a-*` presetId, so without the actionPreset prefix they'd wrongly lose the
  // conversion affordance they had before the id was persisted.
  return (
    action.presetId in BUILT_IN_PRESETS ||
    action.presetId.startsWith('preset-') ||
    action.presetId.startsWith(IX3_ID_PREFIXES.actionPreset)
  );
}

/**
 * Determines if an interaction is a library interaction.
 */
export function isLibraryInteraction(
  interaction: Interaction,
  // NOTE: This is a temporary solution to determine if an interaction is a library interaction.
  // There is a bug for which 'scope.libraryProfileId' is dropped from the interaction document
  // after a round trip to the server.
  // This function is used to determine if an interaction is a library interaction
  // by checking if the component is from a library.
  isComponentFromLibrary: (componentId: string) => boolean
): boolean {
  return (
    interaction.scope.type === 'component' &&
    (interaction.scope.libraryProfileId != null ||
      isComponentFromLibrary(interaction.scope.componentId))
  );
}
